import React, { useState } from "react";
import CustomModal from "../../components/CustomModal";
import { useVatValidation } from "./hooks";
import { isValidPartitaIVA } from "../../utils/vat";
import { useT } from "../../i18n";

interface VerifyVatModalProps {
  open: boolean;
  onClose: () => void;
}

const VerifyVatModal: React.FC<VerifyVatModalProps> = ({ open, onClose }) => {
  const t = useT();
  const [vatNumber, setVatNumber] = useState("");
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [vatExists, setVatExists] = useState<boolean | null>(null);
  const [formatInvalid, setFormatInvalid] = useState(false);
  const { validateVat, isValidating } = useVatValidation();

  const handleVerify = async () => {
    if (!vatNumber.trim()) {
      return;
    }

    setSearchPerformed(true);

    // 1. Formal validation first — reject an invalid Partita IVA before hitting the API
    if (!isValidPartitaIVA(vatNumber)) {
      setFormatInvalid(true);
      setVatExists(null);
      return;
    }
    setFormatInvalid(false);

    // 2. Only if the format is valid, check whether it's already taken
    try {
      const exists = await validateVat(vatNumber.trim());
      setVatExists(exists);
    } catch (error) {
      console.error("VAT verification failed:", error);
      setVatExists(null);
    }
  };

  const handleClose = () => {
    setVatNumber("");
    setSearchPerformed(false);
    setVatExists(null);
    setFormatInvalid(false);
    onClose();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isValidating && vatNumber.trim()) {
      handleVerify();
    }
  };

  return (
    <CustomModal open={open} onClose={handleClose}>
      <div className="flex items-center mb-4">
        <div className="bg-teal-100 text-teal-600 rounded-lg p-3 mr-3">
          <i className="fa-solid fa-clipboard-check text-2xl"></i>
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-0.5">
            {t("deals.verifyVatModal.title")}
          </h2>
          <p className="text-gray-500 text-sm">
            {t("deals.verifyVatModal.subtitle")}
          </p>
        </div>
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t("deals.verifyVatModal.vatLabel")}
        </label>
        <input
          type="text"
          className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-500 text-gray-900 placeholder-gray-400"
          placeholder={t("deals.verifyVatModal.vatPlaceholder")}
          value={vatNumber}
          onChange={(e) => setVatNumber(e.target.value.toUpperCase())}
          onKeyPress={handleKeyPress}
          disabled={isValidating}
        />
        <p className="text-xs text-gray-500 mt-1">
          {t("deals.verifyVatModal.minCharsHint")}
        </p>
      </div>

      {/* Search Results */}
      {searchPerformed && (
        <div className="mb-6">
          {isValidating ? (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600 mr-3"></div>
              <span className="text-gray-600">{t("deals.verifyVatModal.checking")}</span>
            </div>
          ) : formatInvalid ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <i className="fa-solid fa-triangle-exclamation text-red-500 mr-3"></i>
                <div>
                  <p className="text-red-700 font-medium">{t("deals.verifyVatModal.invalidTitle")}</p>
                  <p className="text-red-600 text-sm">
                    {t("deals.verifyVatModal.invalidBody")}
                  </p>
                </div>
              </div>
            </div>
          ) : vatExists === null ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <i className="fa-solid fa-exclamation-triangle text-red-500 mr-3"></i>
                <div>
                  <p className="text-red-700 font-medium">
                    {t("deals.verifyVatModal.failedTitle")}
                  </p>
                  <p className="text-red-600 text-sm">
                    {t("deals.verifyVatModal.failedBody")}
                  </p>
                </div>
              </div>
            </div>
          ) : vatExists ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <i className="fa-solid fa-times-circle text-red-500 mr-3"></i>
                <div>
                  <p className="text-red-700 font-medium">
                    {t("deals.verifyVatModal.takenTitle")}
                  </p>
                  <p className="text-red-600 text-sm">
                    {t("deals.verifyVatModal.takenBody")}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <i className="fa-solid fa-check-circle text-green-500 mr-3"></i>
                <div>
                  <p className="text-green-700 font-medium">
                    {t("deals.verifyVatModal.availableTitle")}
                  </p>
                  <p className="text-green-600 text-sm">
                    {t("deals.verifyVatModal.availableBody")}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end gap-3">
        <button
          className="px-6 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 font-medium transition-colors"
          onClick={handleClose}
          disabled={isValidating}
        >
          {t("deals.verifyVatModal.close")}
        </button>
        <button
          className="px-6 py-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700 font-medium flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleVerify}
          disabled={isValidating || !vatNumber.trim() || vatNumber.length < 5}
        >
          {isValidating ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              {t("deals.verifyVatModal.submitting")}
            </>
          ) : (
            <>
              <i className="fa-solid fa-search"></i>
              {t("deals.verifyVatModal.submit")}
            </>
          )}
        </button>
      </div>
    </CustomModal>
  );
};

export default VerifyVatModal;
